import React, { useState, useCallback } from 'react';
import { API_URL } from '../config';
import './ProductCard.css';

/**
 * Fetches an image as a blob (via proxy for CORS) and returns an HTMLImageElement.
 */
async function loadImageForCanvas(imageUrl, proxyBase) {
  const loadVia = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
      img.src = src;
    });

  // Try proxy first (avoids CORS issues)
  try {
    const proxyUrl = `${proxyBase}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    return await loadVia(proxyUrl);
  } catch {
    // Fall back to direct URL
    return await loadVia(imageUrl);
  }
}

/**
 * Wraps text to fit within maxWidth on the given canvas context.
 */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';

  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/**
 * Draws a rounded rectangle path on the canvas context.
 */
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Composites the product card onto a canvas and returns a blob URL.
 */
async function buildCardBlob(imageUrl, prompt, proxyBase) {
  // Card dimensions: 1080×1350 (4:5 Instagram portrait)
  const W = 1080;
  const H = 1350;
  const PAD = 60;

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ── Background gradient ──────────────────────────────────────────
  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#0a0a0f');
  bg.addColorStop(0.5, '#0f0a1a');
  bg.addColorStop(1, '#0a0a0f');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // ── Subtle star-field dots ────────────────────────────────────────
  ctx.save();
  const rng = (seed) => {
    // Deterministic pseudo-random for consistent output
    let x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };
  for (let i = 0; i < 120; i++) {
    const x = rng(i * 3.1) * W;
    const y = rng(i * 7.3) * H;
    const r = rng(i * 13.7) * 1.5 + 0.3;
    const alpha = rng(i * 2.9) * 0.4 + 0.1;
    ctx.fillStyle = `rgba(255,255,255,${alpha})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();

  // ── Top brand bar ────────────────────────────────────────────────
  const brandH = 80;
  const brandGrad = ctx.createLinearGradient(0, 0, W, 0);
  brandGrad.addColorStop(0, 'rgba(139, 92, 246, 0.15)');
  brandGrad.addColorStop(0.5, 'rgba(167, 139, 250, 0.08)');
  brandGrad.addColorStop(1, 'rgba(139, 92, 246, 0.15)');
  ctx.fillStyle = brandGrad;
  ctx.fillRect(0, 0, W, brandH);

  // Brand name
  ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = '#c4b5fd';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('✨ FashionAI', PAD, brandH / 2);

  // Tagline right
  ctx.font = '22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = 'rgba(196, 181, 253, 0.6)';
  ctx.textAlign = 'right';
  ctx.fillText('fashionai-24n.pages.dev', W - PAD, brandH / 2);

  // ── Design image ─────────────────────────────────────────────────
  const imgTopY = brandH + 40;
  const imgSize = W - PAD * 2;
  const imgX = PAD;

  // Soft glow behind the image
  const glowGrad = ctx.createRadialGradient(W / 2, imgTopY + imgSize / 2, imgSize * 0.1, W / 2, imgTopY + imgSize / 2, imgSize * 0.7);
  glowGrad.addColorStop(0, 'rgba(139, 92, 246, 0.25)');
  glowGrad.addColorStop(1, 'rgba(139, 92, 246, 0)');
  ctx.fillStyle = glowGrad;
  ctx.fillRect(0, imgTopY - 20, W, imgSize + 40);

  try {
    const img = await loadImageForCanvas(imageUrl, proxyBase);

    // Clip to rounded rect
    ctx.save();
    roundRect(ctx, imgX, imgTopY, imgSize, imgSize, 24);
    ctx.clip();
    ctx.drawImage(img, imgX, imgTopY, imgSize, imgSize);
    ctx.restore();

    // Rounded rect border (neon purple glow)
    ctx.save();
    ctx.shadowColor = 'rgba(139, 92, 246, 0.7)';
    ctx.shadowBlur = 24;
    roundRect(ctx, imgX, imgTopY, imgSize, imgSize, 24);
    ctx.strokeStyle = 'rgba(167, 139, 250, 0.5)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  } catch (err) {
    // If image fails to load, show placeholder
    ctx.fillStyle = 'rgba(139, 92, 246, 0.1)';
    roundRect(ctx, imgX, imgTopY, imgSize, imgSize, 24);
    ctx.fill();
    ctx.font = '48px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🎨', W / 2, imgTopY + imgSize / 2);
  }

  // ── Prompt text ──────────────────────────────────────────────────
  const textAreaY = imgTopY + imgSize + 44;
  const textAreaH = H - textAreaY - PAD - 50;

  // Prompt label
  ctx.font = 'bold 22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = 'rgba(167, 139, 250, 0.7)';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('PROMPT', PAD, textAreaY);

  // Divider line
  const lineY = textAreaY + 14;
  const lineGrad = ctx.createLinearGradient(PAD, lineY, W - PAD, lineY);
  lineGrad.addColorStop(0, 'rgba(139, 92, 246, 0.8)');
  lineGrad.addColorStop(1, 'rgba(139, 92, 246, 0)');
  ctx.strokeStyle = lineGrad;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(PAD, lineY);
  ctx.lineTo(W - PAD, lineY);
  ctx.stroke();

  // Prompt text (wrapped)
  const maxPromptLines = Math.floor(textAreaH / 56);
  ctx.font = '34px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = '#e2d9f3';
  ctx.textBaseline = 'top';

  const displayPrompt = prompt || 'No prompt';
  const lines = wrapText(ctx, `"${displayPrompt}"`, W - PAD * 2);
  const clippedLines = lines.slice(0, maxPromptLines);
  if (lines.length > maxPromptLines) {
    clippedLines[maxPromptLines - 1] = clippedLines[maxPromptLines - 1].replace(/"$/, '') + '…"';
  }

  clippedLines.forEach((line, i) => {
    ctx.fillText(line, PAD, textAreaY + 26 + i * 54);
  });

  // ── Bottom bar ───────────────────────────────────────────────────
  const barY = H - PAD - 20;
  ctx.font = '20px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  ctx.fillStyle = 'rgba(167, 139, 250, 0.4)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'bottom';
  ctx.fillText('AI-powered custom merchandise · turn words into wearable art', W / 2, barY);

  // Bottom gradient overlay to fade text into background
  const fadeGrad = ctx.createLinearGradient(0, H - 80, 0, H);
  fadeGrad.addColorStop(0, 'rgba(10,10,15,0)');
  fadeGrad.addColorStop(1, 'rgba(10,10,15,0.4)');
  ctx.fillStyle = fadeGrad;
  ctx.fillRect(0, H - 80, W, 80);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

export default function ProductCard({ imageUrl, prompt }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSave = useCallback(async () => {
    if (!imageUrl || saving) return;
    setSaving(true);
    setSaved(false);

    try {
      const blob = await buildCardBlob(imageUrl, prompt, API_URL);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const slug = (prompt || 'fashionai-design')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .slice(0, 40);
      a.href = objectUrl;
      a.download = `fashionai-card-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Card export failed:', err);
    } finally {
      setSaving(false);
    }
  }, [imageUrl, prompt, saving]);

  if (!imageUrl) return null;

  return (
    <button
      className={`product-card-btn${saving ? ' saving' : ''}${saved ? ' saved' : ''}`}
      onClick={handleSave}
      disabled={saving}
      title="Save a shareable card with your design, prompt, and FashionAI branding"
      aria-label="Save as Card"
    >
      {saving ? (
        <>
          <span className="product-card-spinner" />
          <span>Building card…</span>
        </>
      ) : saved ? (
        <>
          <span>✅</span>
          <span>Card saved!</span>
        </>
      ) : (
        <>
          <span>🃏</span>
          <span>Save as Card</span>
        </>
      )}
    </button>
  );
}
